import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { useUser } from '../Context/UserContext';
import { useCart } from '../Context/CartContext';
import Toast from '../Components/Toast';
import '../Styles/Wishlist.css';

const Wishlist = () => {
    const { user } = useUser();
    const { addToCart } = useCart();
    const [wishlist, setWishlist] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notification, setNotification] = useState(null);
    const [itemToDelete, setItemToDelete] = useState(null);

    useEffect(() => {
        const fetchWishlist = async () => {
            if (!user) {
                setLoading(false);
                return;
            }
            try {
                const res = await axios.get(`${API_BASE_URL}/user/wishlist/${user.id}`);
                setWishlist(res.data);
            } catch (err) {
                console.error('Error fetching wishlist:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchWishlist();
    }, [user]);

    const removeFromWishlist = async () => {
        if (!itemToDelete) return;
        try {
            await axios.delete(`${API_BASE_URL}/user/wishlist/${itemToDelete.id}`);
            setWishlist(wishlist.filter(item => item.id !== itemToDelete.id));
            setNotification({ message: 'Removed from wishlist', type: 'success' });
        } catch (err) {
            console.error('Error removing from wishlist:', err);
            setNotification({ message: 'Failed to remove item', type: 'error' });
        } finally {
            setItemToDelete(null);
        }
    };

    const handleMoveToCart = (item) => {
        const product = {
            id: item.product_id,
            name: item.productName,
            Price: item.Price,
            photo: item.productPhoto
        };
        const variation = item.variation_id ? {
            varID: item.variation_id,
            photo: item.variationPhoto,
            SizeName: item.SizeName,
            ColorName: item.ColorName
        } : null;

        addToCart(product, variation, 1);
        removeFromWishlist(item.id);
        setNotification({ message: 'Moved to bag!', type: 'success' });
    };

    if (!user) {
        return (
            <div className="wishlist-empty-container">
                <div className="wishlist-empty-content">
                    <h2 className="wishlist-title">My Wishlist</h2>
                    <p className="wishlist-empty-message">Please log in to view your wishlist.</p>
                    <Link to="/auth" className="wishlist-login-btn">Log In / Sign Up</Link>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="wishlist-loading">
                <div className="loader"></div>
            </div>
        );
    }

    return (
        <div className="wishlist-page">
            <div className="wishlist-container">
                <header className="wishlist-header">
                    <h1 className="wishlist-title">My Wishlist</h1>
                    <p className="wishlist-count">{wishlist.length} item{wishlist.length !== 1 ? 's' : ''}</p>
                </header>

                {wishlist.length === 0 ? (
                    <div className="wishlist-empty-state">
                        <div className="wishlist-empty-icon">
                            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                            </svg>
                        </div>
                        <h3>Your wishlist is empty</h3>
                        <p>Save items you love to your wishlist and they'll appear here.</p>
                        <Link to="/shop" className="wishlist-shop-btn">Continue Shopping</Link>
                    </div>
                ) : (
                    <div className="wishlist-grid">
                        {wishlist.map((item) => (
                            <div key={item.id} className="wishlist-item">
                                <button
                                    className="wishlist-remove-btn"
                                    onClick={() => setItemToDelete(item)}
                                    aria-label="Remove from wishlist"
                                >
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="18" y1="6" x2="6" y2="18"></line>
                                        <line x1="6" y1="6" x2="18" y2="18"></line>
                                    </svg>
                                </button>
                                <div className="wishlist-image-wrapper">
                                    <img
                                        src={`${API_BASE_URL}${item.variationPhoto || item.productPhoto}`}
                                        alt={item.productName}
                                        className="wishlist-image"
                                    />
                                </div>
                                <div className="wishlist-details">
                                    <h3 className="wishlist-item-name">{item.productName}</h3>
                                    {(item.SizeName || item.ColorName) && (
                                        <p className="wishlist-item-variants">
                                            {item.ColorName && <span>Color: {item.ColorName}</span>}
                                            {item.ColorName && item.SizeName && <span className="variant-sep">|</span>}
                                            {item.SizeName && <span>Size: {item.SizeName}</span>}
                                        </p>
                                    )}
                                    <p className="wishlist-item-price">${parseFloat(item.Price).toFixed(2)}</p>
                                    <button
                                        className="wishlist-add-to-cart-btn"
                                        onClick={() => handleMoveToCart(item)}
                                    >
                                        Add to Bag
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Confirmation Modal */}
            {itemToDelete && (
                <div className="wishlist-modal-overlay" onClick={() => setItemToDelete(null)}>
                    <div className="wishlist-modal" onClick={e => e.stopPropagation()}>
                        <div className="wishlist-modal-content">
                            <div className="wishlist-modal-icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M3 6h18m-2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                    <line x1="10" y1="11" x2="10" y2="17"></line>
                                    <line x1="14" y1="11" x2="14" y2="17"></line>
                                </svg>
                            </div>
                            <h3>Remove from Wishlist?</h3>
                            <p>Are you sure you want to remove <strong>{itemToDelete.productName}</strong> from your wishlist?</p>
                            <div className="wishlist-modal-actions">
                                <button className="wishlist-modal-cancel" onClick={() => setItemToDelete(null)}>Cancel</button>
                                <button className="wishlist-modal-confirm" onClick={removeFromWishlist}>Remove</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {notification && (
                <Toast
                    message={notification.message}
                    type={notification.type}
                    onClose={() => setNotification(null)}
                />
            )}
        </div>
    );
};

export default Wishlist;
